/* eslint-disable no-console */
/**
 * i18n 数据加载中间件（可配置版本）
 */

const { getUrlCache, setUrlCache } = require('./cache');
const { toCmsLang, extractLangFromPath } = require('./lang-mapper');
const { TIMEOUT_CONFIG } = require('./constants');

/**
 * 带超时控制的 Promise 包装器
 * @param {Promise} promise - 原始 Promise
 * @param {number} timeout - 超时时间（毫秒）
 * @returns {Promise} 带超时控制的 Promise
 */
function withTimeout(promise, timeout = 5000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`Request timeout after ${timeout}ms`)), timeout)),
  ]);
}

/**
 * 获取单个 i18n 数据（返回 CDN URL，使用缓存）
 * @param {object} TranslationAjax - 翻译 API 实例
 * @param {number|string} routeId - 路由ID
 * @param {string} locale - Next.js locale (如 'ind')
 * @param {boolean} skipCache - 是否跳过 URL 缓存（默认 false）
 * @returns {Promise<string|object>} CDN URL 或空对象
 */
async function fetchI18nData(TranslationAjax, routeId, locale, skipCache = false) {
  console.log(`[fetchI18nData] Called with routeId: ${routeId}, locale: ${locale}, skipCache: ${skipCache}`);

  // 1. 先查缓存（使用 Next.js locale 作为缓存键）
  if (!skipCache) {
    const cachedUrl = getUrlCache(routeId, locale);
    if (cachedUrl) {
      console.log(`[fetchI18nData] Cache hit for ${routeId}/${locale}`);
      return cachedUrl;
    }
  }

  // 2. 缓存未命中，调用 API（带超时控制）
  // 🔑 关键：将 Next.js locale 转换为 CMS 语言标识
  const cmsLang = toCmsLang(locale);
  console.log(`[fetchI18nData] Language mapping: ${locale} → ${cmsLang}`);

  try {
    const result = await withTimeout(
      TranslationAjax.getTranslate({
        id: routeId,
        lang: cmsLang, // 使用转换后的 CMS 语言标识
        isPreview: false,
      }),
      TIMEOUT_CONFIG.API,
    );

    console.log(`[fetchI18nData] API response for ${routeId}/${locale}, code: ${result?.code}, hasData: ${!!result?.data}`);

    if (result && result.code === 0) {
      const url = result.data || {};

      // 3. 缓存 URL（使用 Next.js locale 作为缓存键，保持一致）
      // 如果 skipCache=true，则不写入缓存
      if (!skipCache) {
        setUrlCache(routeId, locale, url);
      }

      console.log(`[fetchI18nData] Returning URL for ${routeId}/${locale}: ${url}`);
      return url;
    }

    console.warn(
      `[fetchI18nData] Failed to fetch data for ${routeId}/${locale} (CMS: ${cmsLang}), code:`,
      result?.code,
    );
    return {};
  } catch (error) {
    console.error(`[fetchI18nData] Error fetching i18n data for ${routeId}/${locale}:`, error);
    return {};
  }
}

/**
 * 创建 i18n 中间件
 * @param {object} config - 配置对象
 * @param {object} config.TranslationAjax - 翻译 API 实例
 * @param {object} config.manifest - 命名空间与 routeId 的映射
 * @param {string[]} config.namespaces - 需要加载的命名空间列表
 * @returns {Function} Express 中间件函数
 */
function createI18nMiddleware(config) {
  const { TranslationAjax, manifest, namespaces } = config;

  if (!TranslationAjax) {
    throw new Error('[i18n-middleware] TranslationAjax is required');
  }

  if (!manifest || Object.keys(manifest).length === 0) {
    throw new Error('[i18n-middleware] manifest is required');
  }

  if (!namespaces || namespaces.length === 0) {
    throw new Error('[i18n-middleware] namespaces is required');
  }

  return async (req, res, next) => {
    // 跳过静态资源和 API 路由
    if (
      req.path.startsWith('/_next/')
      || req.path.startsWith('/api/')
      || req.path.startsWith('/static/')
      || req.path.includes('.')
    ) {
      return next();
    }

    // 🔑 获取语言：优先使用 Next.js 注入的 locale,fallback 到路径解析
    // Next.js 的 i18n 中间件会在 req 上设置 locale 属性
    // 但在自定义服务器中,需要从 URL、cookies 或其他来源获取
    let { locale } = req; // Next.js 可能会设置这个属性

    // 如果没有 req.locale,尝试从原始 URL 解析
    if (!locale) {
      // req.originalUrl 包含完整的 URL (包括语言前缀)
      const originalUrl = req.originalUrl || req.url || req.path;
      locale = extractLangFromPath(originalUrl);
    }

    console.log(`[i18n-loader] Language detected: ${locale}, path: ${req.path}, originalUrl: ${req.originalUrl || req.url}`);

    // 并行加载所有 ns 的 CDN URL
    const loadTasks = namespaces.map((ns) => {
      const routeId = manifest[ns]?.routeId;
      if (!routeId) {
        console.warn(`[i18n-loader] No routeId found for namespace: ${ns}`);
        return Promise.resolve({ ns, data: {} });
      }
      return fetchI18nData(TranslationAjax, routeId, locale).then((data) => ({ ns, data }));
    });

    try {
      const results = await Promise.all(loadTasks);

      // 组装结果
      const resources = {};
      resources[locale] = {};
      results.forEach(({ ns, data }) => {
        resources[locale][ns] = data;
      });

      // 注入到 req 对象，供 _app.js 使用
      req.$i18nData = {
        resources,
        ns: namespaces,
        lang: locale,
      };

      console.log(`[i18n-loader] i18n data loaded successfully for locale: ${locale}`);
    } catch (error) {
      console.error('[i18n-loader] Error loading i18n data:', error);
      // 降级：注入空数据
      req.$i18nData = {
        resources: { [locale]: {} },
        ns: [],
        lang: locale,
      };
    }

    next();
  };
}

module.exports = {
  createI18nMiddleware,
  fetchI18nData,
  withTimeout,
};
